import React, { useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { X, Warning } from '@phosphor-icons/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { emailEditorModules } from 'shared/config/quillSetup';
import { useMassMailContext } from '../context/MassMailContext';
import { useMassMailData } from '../hooks/useMassMailData';

// ─── Shared ───────────────────────────────────────────────────────────────────

const NEW_TYPE_KEY = '__new__';

const LANGUAGE_OPTIONS = ['NL', 'EN', 'FR', 'DE'];

const nativeSelectClass =
  'w-full h-8 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50';

// ─── QuickCreateTemplateForm ───────────────────────────────────────────────────

interface QuickCreateTemplateFormProps {
  onClose: () => void;
  isContextMode?: boolean;
}

export const QuickCreateTemplateForm: React.FC<QuickCreateTemplateFormProps> = ({
  onClose,
  isContextMode = false,
}) => {
  const { session } = useSession();
  const { state, dispatch } = useMassMailContext();
  const { fetchTemplates, fetchTemplateTypes } = useMassMailData();

  const [label, setLabel] = useState('NL');
  const [templateType, setTemplateType] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [subject, setSubject] = useState(state.emailSubject || '');
  const [content, setContent] = useState(state.emailContent || '');
  const [isLoading, setIsLoading] = useState(false);

  const isNewType = templateType === NEW_TYPE_KEY;
  const resolvedTypeName = isNewType ? newTypeName.trim() : templateType;

  const handleSave = async () => {
    if (!session) return;
    if (!subject.trim()) { toast.error('Vul een naam / onderwerp in'); return; }
    if (!resolvedTypeName) { toast.error('Selecteer of maak een categorie aan'); return; }

    setIsLoading(true);
    try {
      if (isNewType) {
        const typeRes = await fetchWithAuth(
          `${API_URL}/mail-templates/types`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: resolvedTypeName }) },
          session
        );
        if (!typeRes.ok) {
          const err = await typeRes.json().catch(() => ({}));
          throw new Error(err.message || 'Kon categorie niet aanmaken');
        }
      }

      const templateRes = await fetchWithAuth(
        `${API_URL}/mail-templates`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label, templateType: resolvedTypeName, subject: subject.trim(), content }) },
        session
      );
      if (!templateRes.ok) {
        const err = await templateRes.json().catch(() => ({}));
        throw new Error(err.message || 'Kon template niet opslaan');
      }

      const newTemplate = await templateRes.json();
      await fetchTemplateTypes();
      await fetchTemplates();

      if (!isContextMode) {
        dispatch({ type: 'SET_SELECTED_TYPE', payload: resolvedTypeName });
        dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: String(newTemplate.id) });
        dispatch({ type: 'SET_EMAIL_SUBJECT', payload: subject.trim() });
        dispatch({ type: 'SET_EMAIL_CONTENT', payload: content });
        toast.success('Template aangemaakt en geselecteerd');
      } else {
        toast.success('Template opgeslagen. Beschikbaar bij standaard mail templates.');
      }
      onClose();
    } catch (error) {
      toast.error(`Fout bij opslaan: ${error instanceof Error ? error.message : 'Onbekende fout'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-4 rounded-md border border-blue-200 bg-blue-50/50 p-4 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-blue-800">Nieuw template</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Sluiten">
          <X size={15} />
        </button>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Categorie</Label>
        <select
          className={nativeSelectClass}
          value={templateType}
          onChange={(e) => setTemplateType(e.target.value)}
        >
          <option value="">Selecteer een categorie...</option>
          {state.templateTypes.map((type) => (
            <option key={type.name} value={type.name}>{type.name}</option>
          ))}
          <option value={NEW_TYPE_KEY}>+ Nieuwe categorie aanmaken</option>
        </select>
        {isNewType && (
          <Input
            placeholder="Naam nieuwe categorie..."
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Taal / variant</Label>
        <select
          className={nativeSelectClass}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        >
          {LANGUAGE_OPTIONS.map((lang) => (
            <option key={lang} value={lang}>{lang}</option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Naam / onderwerp</Label>
        <Input
          placeholder="Bijv. Vraag voor extra foto's – Ticket #{ticketId}"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="h-8 text-sm"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Inhoud</Label>
        <div className="border rounded-md overflow-hidden bg-white">
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={emailEditorModules}
            style={{ minHeight: '160px', fontSize: '13px' }}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onClose} disabled={isLoading}>Annuleren</Button>
        <Button size="sm" onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Opslaan...' : 'Opslaan'}
        </Button>
      </div>
    </div>
  );
};

// ─── QuickDeleteTemplateForm ───────────────────────────────────────────────────

interface QuickDeleteTemplateFormProps {
  onClose: () => void;
}

export const QuickDeleteTemplateForm: React.FC<QuickDeleteTemplateFormProps> = ({ onClose }) => {
  const { session } = useSession();
  const { state } = useMassMailContext();
  const { fetchTemplates, fetchTemplateTypes } = useMassMailData();

  const [selectedId, setSelectedId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const deletableTemplates = state.templates;
  const selectedTemplate = deletableTemplates.find((t) => String(t.id) === selectedId);

  const getDisplayText = (t: typeof deletableTemplates[0]) => {
    const name = t.subject || t.label || 'Naamloos template';
    return t.label ? `${name} (${t.label}) — ${t.templateType}` : `${name} — ${t.templateType}`;
  };

  const handleDelete = async () => {
    if (!session || !selectedTemplate) return;
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/mail-templates/${selectedTemplate.id}`,
        { method: 'DELETE' },
        session
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Kon template niet verwijderen');
      }
      await fetchTemplateTypes();
      await fetchTemplates();
      toast.success(`Template "${selectedTemplate.subject}" verwijderd`);
      onClose();
    } catch (error) {
      toast.error(`Fout bij verwijderen: ${error instanceof Error ? error.message : 'Onbekende fout'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-4 rounded-md border border-red-200 bg-red-50/50 p-4 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-red-800">Template verwijderen</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Sluiten">
          <X size={15} />
        </button>
      </div>

      {deletableTemplates.length === 0 ? (
        <p className="text-sm text-gray-500">Geen database-templates beschikbaar om te verwijderen.</p>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label className="text-xs">Selecteer template</Label>
            <select
              className={nativeSelectClass}
              value={selectedId}
              onChange={(e) => { setSelectedId(e.target.value); setConfirmed(false); }}
            >
              <option value="">Kies een template...</option>
              {deletableTemplates.map((t) => (
                <option key={String(t.id)} value={String(t.id)}>
                  {getDisplayText(t)}
                </option>
              ))}
            </select>
          </div>

          {selectedTemplate && (
            <div className="rounded-md border border-red-300 bg-red-100 px-3 py-2 flex items-start gap-2">
              <Warning size={15} weight="fill" className="shrink-0 mt-0.5 text-red-600" />
              <div className="space-y-1">
                <p className="text-xs text-red-800 font-medium">
                  "{selectedTemplate.subject}" wordt permanent verwijderd.
                </p>
                <label className="flex items-center gap-1.5 text-xs text-red-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="accent-red-600"
                  />
                  Ik begrijp dat dit niet ongedaan gemaakt kan worden
                </label>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} disabled={isLoading}>Annuleren</Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              disabled={!selectedId || !confirmed || isLoading}
            >
              {isLoading ? 'Verwijderen...' : 'Verwijderen'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
